import React from "react";
import {
  Modal,
  Typography,
  Card,
  List,
  Empty,
  Badge,
  Button,
  Divider,
  Space,
} from "antd";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { useBookingWizard } from "../../../contexts/BookingWizardContext";
import {
  formatEuro,
  formatWeight,
  getStatusColor,
} from "../PricingCalculatorHelpers";
import { PickupRequest, BookingSupplier } from "../PricingCalculatorTypes";

const { Text } = Typography;

interface PickupRequestModalsProps {
  isPickupRequestModalVisible: boolean;
  setIsPickupRequestModalVisible: (visible: boolean) => void;
  isRequestingPickup: boolean;
  handlePickupFeeRequest: () => void;
  bookingSupplier: BookingSupplier | null;
  transportType: string;
  incoterm: string;
  isPickupDetailsModalVisible: boolean;
  setIsPickupDetailsModalVisible: (visible: boolean) => void;
  pickupRequest: PickupRequest | null;
  requestNewPickupFeeQuote: () => void;
  acceptPickupFeeOffer: () => void;
  pickupRequestHistory: PickupRequest[];
  showHistoryRequestDetails: (request: PickupRequest) => void;
  getStatusText: (status: string) => string;
  isHistoryDetailsModalVisible: boolean;
  setIsHistoryDetailsModalVisible: (visible: boolean) => void;
  selectedHistoryRequest: PickupRequest | null;
}

export const PickupRequestModals: React.FC<PickupRequestModalsProps> = ({
  isPickupRequestModalVisible,
  setIsPickupRequestModalVisible,
  isRequestingPickup,
  handlePickupFeeRequest,
  bookingSupplier,
  transportType,
  incoterm,
  isPickupDetailsModalVisible,
  setIsPickupDetailsModalVisible,
  pickupRequest,
  requestNewPickupFeeQuote,
  acceptPickupFeeOffer,
  pickupRequestHistory,
  showHistoryRequestDetails,
  getStatusText,
  isHistoryDetailsModalVisible,
  setIsHistoryDetailsModalVisible,
  selectedHistoryRequest,
}) => {
  const { state } = useBookingWizard();
  const { t } = useTranslation();

  return (
    <>
      {/* Pickup Fee Request Modal */}
      <Modal
        title={t("requestPickupFeeQuote", "Request Pickup Fee Quote")}
        open={isPickupRequestModalVisible}
        onOk={handlePickupFeeRequest}
        onCancel={() => setIsPickupRequestModalVisible(false)}
        confirmLoading={isRequestingPickup}
        okText={t("submitRequest", "Submit Request")}
        cancelText={t("cancel", "Cancel")}
        width={600}
      >
        <Typography.Paragraph>
          {t(
            "pickupRequestDescription",
            "Our team will review your request and provide a quote for pickup service from your supplier. You will be notified once the quote is ready."
          )}
        </Typography.Paragraph>

        <Divider orientation="left">
          {t("requestDetails", "Request Details")}
        </Divider>

        <Card
          size="small"
          title={t("supplierInformation", "Supplier Information")}
          style={{ marginBottom: 16 }}
        >
          {bookingSupplier ? (
            <List size="small">
              <List.Item>
                <Text strong>{t("supplierName")}:</Text>
                <Text>{bookingSupplier.name}</Text>
              </List.Item>
              <List.Item>
                <Text strong>{t("location")}:</Text>
                <Text>{`${bookingSupplier.city}, ${bookingSupplier.country}`}</Text>
              </List.Item>
              <List.Item>
                <Text strong>{t("address")}:</Text>
                <Text>{bookingSupplier.address}</Text>
              </List.Item>
            </List>
          ) : (
            <Empty
              description={t(
                "noSupplierInformation",
                "No supplier information available"
              )}
            />
          )}
        </Card>

        <Card
          size="small"
          title={t("cargoInformation", "Cargo Information")}
          style={{ marginBottom: 16 }}
        >
          <List size="small">
            <List.Item>
              <Text strong>{t("totalWeight")}:</Text>
              <Text>
                {formatWeight(
                  state.products?.reduce(
                    (sum, product) => sum + (product.gross_weight || 0),
                    0
                  ) || 0
                )}
              </Text>
            </List.Item>
            <List.Item>
              <Text strong>{t("totalVolume")}:</Text>
              <Text>
                {state.products
                  ?.reduce((sum, product) => sum + (product.cbm || 0), 0)
                  ?.toFixed(2)}{" "}
                m³
              </Text>
            </List.Item>
            <List.Item>
              <Text strong>{t("totalCartons")}:</Text>
              <Text>
                {state.products?.reduce(
                  (sum, product) => sum + (product.cartons || 0),
                  0
                ) || 0}
              </Text>
            </List.Item>
            <List.Item>
              <Text strong>{t("totalPieces")}:</Text>
              <Text>
                {state.products?.reduce(
                  (sum, product) => sum + (product.pieces || 0),
                  0
                ) || 0}
              </Text>
            </List.Item>
          </List>
        </Card>

        <Card size="small" title={t("transportDetails", "Transport Details")}>
          <List size="small">
            <List.Item>
              <Text strong>{t("transportType")}:</Text>
              <Text>{transportType}</Text>
            </List.Item>
            <List.Item>
              <Text strong>{t("incoterm")}:</Text>
              <Text>{incoterm}</Text>
            </List.Item>
            <List.Item>
              <Text strong>{t("cargoReadinessDate")}:</Text>
              <Text>
                {state.origin_pickup_date
                  ? dayjs(state.origin_pickup_date).format("DD/MM/YYYY")
                  : t("notSpecified")}
              </Text>
            </List.Item>
          </List>
        </Card>

        <Typography.Paragraph type="secondary" style={{ marginTop: 16 }}>
          {t(
            "pickupRequestNote",
            "Note: This request is specific to your current booking and supplier location."
          )}
        </Typography.Paragraph>
      </Modal>

      {/* Pickup Request Details Modal */}
      <Modal
        title={t("pickupRequestDetails", "Pickup Request Details")}
        open={isPickupDetailsModalVisible}
        onCancel={() => setIsPickupDetailsModalVisible(false)}
        footer={
          pickupRequest?.status === "offer"
            ? [
                <Button key="request_new" onClick={requestNewPickupFeeQuote}>
                  {t("requestNewQuote", "Request New Quote")}
                </Button>,
                <Button
                  key="cancel"
                  onClick={() => setIsPickupDetailsModalVisible(false)}
                >
                  {t("cancel", "Cancel")}
                </Button>,
                <Button
                  key="accept"
                  type="primary"
                  onClick={acceptPickupFeeOffer}
                >
                  {t("acceptOffer", "Accept Offer")}
                </Button>,
              ]
            : pickupRequest?.status === "accepted"
            ? [
                <Button key="request_new" onClick={requestNewPickupFeeQuote}>
                  {t("requestNewQuote", "Request New Quote")}
                </Button>,
                <Button
                  key="close"
                  type="primary"
                  onClick={() => setIsPickupDetailsModalVisible(false)}
                >
                  {t("close", "Close")}
                </Button>,
              ]
            : [
                <Button
                  key="close"
                  type="primary"
                  onClick={() => setIsPickupDetailsModalVisible(false)}
                >
                  {t("close", "Close")}
                </Button>,
              ]
        }
        width={600}
      >
        {pickupRequest && (
          <>
            <div style={{ marginBottom: 16 }}>
              <Badge
                status={getStatusColor(pickupRequest.status)}
                text={
                  <Typography.Text strong style={{ fontSize: "16px" }}>
                    {getStatusText(pickupRequest.status)}
                  </Typography.Text>
                }
              />
              <Typography.Text
                type="secondary"
                style={{ display: "block", marginTop: 8 }}
              >
                {t("requestedOn", "Requested on")}:{" "}
                {dayjs(pickupRequest.created_at).format("DD/MM/YYYY HH:mm")}
              </Typography.Text>

              {pickupRequest.offer_at && (
                <Typography.Text
                  type="secondary"
                  style={{ display: "block", marginTop: 4 }}
                >
                  {t("offerProvidedOn", "Offer provided on")}:{" "}
                  {dayjs(pickupRequest.offer_at).format("DD/MM/YYYY HH:mm")}
                </Typography.Text>
              )}
            </div>

            {pickupRequest.status === "offer" && pickupRequest.cost && (
              <Card
                style={{
                  marginBottom: 16,
                  backgroundColor: "#f6ffed",
                  borderColor: "#b7eb8f",
                }}
              >
                <Typography.Title
                  level={5}
                  style={{ color: "#52c41a", margin: 0 }}
                >
                  {t("pickupOfferPrice", "Offered Price")}:{" "}
                  {formatEuro(pickupRequest.cost)}
                </Typography.Title>
              </Card>
            )}

            {pickupRequest.status === "accepted" && pickupRequest.cost && (
              <Card
                style={{
                  marginBottom: 16,
                  backgroundColor: "#f0f5ff",
                  borderColor: "#adc6ff",
                }}
              >
                <Typography.Title
                  level={5}
                  style={{ color: "#1890ff", margin: 0 }}
                >
                  {t("acceptedPickupFee", "Accepted Pickup Fee")}:{" "}
                  {formatEuro(pickupRequest.cost)}
                </Typography.Title>
              </Card>
            )}

            {pickupRequest.notes && (
              <Card
                style={{
                  marginBottom: 16,
                  backgroundColor: "#f5f5f5",
                  borderColor: "#d9d9d9",
                }}
              >
                <Typography.Title level={5} style={{ margin: 0 }}>
                  {t("notes", "Notes")}
                </Typography.Title>
                <Typography.Paragraph>
                  {pickupRequest.notes}
                </Typography.Paragraph>
              </Card>
            )}

            <Divider orientation="left">
              {t("requestDetails", "Request Details")}
            </Divider>

            <Card
              size="small"
              title={t("supplierInformation", "Supplier Information")}
              style={{ marginBottom: 16 }}
            >
              {pickupRequest.booking_blob.supplier ? (
                <List size="small">
                  <List.Item>
                    <Text strong>{t("supplierName")}:</Text>
                    <Text>{pickupRequest.booking_blob.supplier.name}</Text>
                  </List.Item>
                  <List.Item>
                    <Text strong>{t("location")}:</Text>
                    <Text>{`${pickupRequest.booking_blob.supplier.city}, ${pickupRequest.booking_blob.supplier.country}`}</Text>
                  </List.Item>
                  <List.Item>
                    <Text strong>{t("address")}:</Text>
                    <Text>{pickupRequest.booking_blob.supplier.address}</Text>
                  </List.Item>
                </List>
              ) : (
                <Empty
                  description={t(
                    "noSupplierInformation",
                    "No supplier information available"
                  )}
                />
              )}
            </Card>

            <Card
              size="small"
              title={t("cargoInformation", "Cargo Information")}
              style={{ marginBottom: 16 }}
            >
              <List size="small">
                <List.Item>
                  <Text strong>{t("totalWeight")}:</Text>
                  <Text>
                    {formatWeight(
                      pickupRequest.booking_blob.products_summary
                        .total_weight || 0
                    )}
                  </Text>
                </List.Item>
                <List.Item>
                  <Text strong>{t("totalVolume")}:</Text>
                  <Text>
                    {pickupRequest.booking_blob.products_summary.total_cbm?.toFixed(
                      2
                    ) || "0.00"}{" "}
                    m³
                  </Text>
                </List.Item>
                <List.Item>
                  <Text strong>{t("totalCartons")}:</Text>
                  <Text>
                    {pickupRequest.booking_blob.products_summary
                      .total_cartons || 0}
                  </Text>
                </List.Item>
                <List.Item>
                  <Text strong>{t("totalPieces")}:</Text>
                  <Text>
                    {pickupRequest.booking_blob.products_summary.total_pieces ||
                      0}
                  </Text>
                </List.Item>
              </List>
            </Card>

            <Card
              size="small"
              title={t("transportDetails", "Transport Details")}
            >
              <List size="small">
                <List.Item>
                  <Text strong>{t("transportType")}:</Text>
                  <Text>
                    {pickupRequest.booking_blob.transport_info.transport_type}
                  </Text>
                </List.Item>
                <List.Item>
                  <Text strong>{t("incoterm")}:</Text>
                  <Text>
                    {pickupRequest.booking_blob.transport_info.incoterm}
                  </Text>
                </List.Item>
                <List.Item>
                  <Text strong>{t("cargoReadinessDate")}:</Text>
                  <Text>
                    {pickupRequest.booking_blob.transport_info
                      .origin_pickup_date
                      ? dayjs(
                          pickupRequest.booking_blob.transport_info
                            .origin_pickup_date
                        ).format("DD/MM/YYYY")
                      : t("notSpecified")}
                  </Text>
                </List.Item>
              </List>
            </Card>

            {pickupRequestHistory.length > 1 && (
              <div style={{ marginTop: 16 }}>
                <Divider orientation="left">
                  {t("requestHistorySummary", "Request History")}
                </Divider>

                <List
                  itemLayout="horizontal"
                  dataSource={pickupRequestHistory.slice(1)}
                  renderItem={(item) => (
                    <List.Item
                      actions={[
                        <Button
                          key="view_details"
                          type="link"
                          onClick={() => showHistoryRequestDetails(item)}
                        >
                          {t("viewDetails", "View Details")}
                        </Button>,
                      ]}
                    >
                      <List.Item.Meta
                        avatar={<Badge status={getStatusColor(item.status)} />}
                        title={
                          <Space>
                            <span>{getStatusText(item.status)}</span>
                            {item.cost && (
                              <Text type="success">
                                {formatEuro(item.cost)}
                              </Text>
                            )}
                          </Space>
                        }
                        description={dayjs(item.created_at).format(
                          "DD/MM/YYYY HH:mm"
                        )}
                      />
                    </List.Item>
                  )}
                />
              </div>
            )}
          </>
        )}
      </Modal>

      {/* Historical Pickup Request Details Modal */}
      <Modal
        title={t("historicalRequestDetails", "Historical Request Details")}
        open={isHistoryDetailsModalVisible}
        onCancel={() => setIsHistoryDetailsModalVisible(false)}
        footer={[
          <Button
            key="close"
            type="primary"
            onClick={() => setIsHistoryDetailsModalVisible(false)}
          >
            {t("close", "Close")}
          </Button>,
        ]}
        width={600}
      >
        {selectedHistoryRequest && (
          <>
            <div style={{ marginBottom: 16 }}>
              <Badge
                status={getStatusColor(selectedHistoryRequest.status)}
                text={
                  <Typography.Text strong style={{ fontSize: "16px" }}>
                    {getStatusText(selectedHistoryRequest.status)}
                  </Typography.Text>
                }
              />
              <Typography.Text
                type="secondary"
                style={{ display: "block", marginTop: 8 }}
              >
                {t("requestedOn", "Requested on")}:{" "}
                {dayjs(selectedHistoryRequest.created_at).format(
                  "DD/MM/YYYY HH:mm"
                )}
              </Typography.Text>

              {selectedHistoryRequest.offer_at && (
                <Typography.Text
                  type="secondary"
                  style={{ display: "block", marginTop: 4 }}
                >
                  {t("offerProvidedOn", "Offer provided on")}:{" "}
                  {dayjs(selectedHistoryRequest.offer_at).format(
                    "DD/MM/YYYY HH:mm"
                  )}
                </Typography.Text>
              )}
            </div>

            {selectedHistoryRequest.status === "offer" &&
              selectedHistoryRequest.cost && (
                <Card
                  style={{
                    marginBottom: 16,
                    backgroundColor: "#f6ffed",
                    borderColor: "#b7eb8f",
                  }}
                >
                  <Typography.Title
                    level={5}
                    style={{ color: "#52c41a", margin: 0 }}
                  >
                    {t("pickupOfferPrice", "Offered Price")}:{" "}
                    {formatEuro(selectedHistoryRequest.cost)}
                  </Typography.Title>
                </Card>
              )}

            {selectedHistoryRequest.notes && (
              <Card
                style={{
                  marginBottom: 16,
                  backgroundColor: "#f5f5f5",
                  borderColor: "#d9d9d9",
                }}
              >
                <Typography.Title level={5} style={{ margin: 0 }}>
                  {t("notes", "Notes")}
                </Typography.Title>
                <Typography.Paragraph>
                  {selectedHistoryRequest.notes}
                </Typography.Paragraph>
              </Card>
            )}

            <Divider orientation="left">
              {t("requestDetails", "Request Details")}
            </Divider>

            <Card
              size="small"
              title={t("supplierInformation", "Supplier Information")}
              style={{ marginBottom: 16 }}
            >
              {selectedHistoryRequest.booking_blob.supplier ? (
                <List size="small">
                  <List.Item>
                    <Text strong>{t("supplierName")}:</Text>
                    <Text>
                      {selectedHistoryRequest.booking_blob.supplier.name}
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text strong>{t("location")}:</Text>
                    <Text>{`${selectedHistoryRequest.booking_blob.supplier.city}, ${selectedHistoryRequest.booking_blob.supplier.country}`}</Text>
                  </List.Item>
                  <List.Item>
                    <Text strong>{t("address")}:</Text>
                    <Text>
                      {selectedHistoryRequest.booking_blob.supplier.address}
                    </Text>
                  </List.Item>
                </List>
              ) : (
                <Empty
                  description={t(
                    "noSupplierInformation",
                    "No supplier information available"
                  )}
                />
              )}
            </Card>

            <Card
              size="small"
              title={t("cargoInformation", "Cargo Information")}
              style={{ marginBottom: 16 }}
            >
              <List size="small">
                <List.Item>
                  <Text strong>{t("totalWeight")}:</Text>
                  <Text>
                    {formatWeight(
                      selectedHistoryRequest.booking_blob.products_summary
                        .total_weight || 0
                    )}
                  </Text>
                </List.Item>
                <List.Item>
                  <Text strong>{t("totalVolume")}:</Text>
                  <Text>
                    {selectedHistoryRequest.booking_blob.products_summary.total_cbm?.toFixed(
                      2
                    ) || "0.00"}{" "}
                    m³
                  </Text>
                </List.Item>
                <List.Item>
                  <Text strong>{t("totalCartons")}:</Text>
                  <Text>
                    {selectedHistoryRequest.booking_blob.products_summary
                      .total_cartons || 0}
                  </Text>
                </List.Item>
                <List.Item>
                  <Text strong>{t("totalPieces")}:</Text>
                  <Text>
                    {selectedHistoryRequest.booking_blob.products_summary
                      .total_pieces || 0}
                  </Text>
                </List.Item>
              </List>
            </Card>

            <Card
              size="small"
              title={t("transportDetails", "Transport Details")}
            >
              <List size="small">
                <List.Item>
                  <Text strong>{t("transportType")}:</Text>
                  <Text>
                    {
                      selectedHistoryRequest.booking_blob.transport_info
                        .transport_type
                    }
                  </Text>
                </List.Item>
                <List.Item>
                  <Text strong>{t("incoterm")}:</Text>
                  <Text>
                    {
                      selectedHistoryRequest.booking_blob.transport_info
                        .incoterm
                    }
                  </Text>
                </List.Item>
                <List.Item>
                  <Text strong>{t("cargoReadinessDate")}:</Text>
                  <Text>
                    {selectedHistoryRequest.booking_blob.transport_info
                      .origin_pickup_date
                      ? dayjs(
                          selectedHistoryRequest.booking_blob.transport_info
                            .origin_pickup_date
                        ).format("DD/MM/YYYY")
                      : t("notSpecified")}
                  </Text>
                </List.Item>
              </List>
            </Card>
          </>
        )}
      </Modal>
    </>
  );
};















